import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { useNotification } from "@/context/NotificationContext";
import {
  courseFormSchema,
  CourseFormData,
} from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/types";
import {
  emptyCourseFormData,
  formDataToPayload,
} from "@/modules/learningCenter/features/CourseManagement/components/CourseForm/mapper";
import { fetchCourseMetadataAction } from "@/modules/learningCenter/features/CourseManagement/slices/courseListSlice";
import {
  createCourseAction,
  resetCreateUpdateCourseState,
} from "@/modules/learningCenter/features/CourseManagement/slices/createUpdateCourseSlice";

export const useCreateCourse = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const metadata = useAppSelector(
    (state: RootState) => state.courseList.metadata,
  );
  const metadataLoading = useAppSelector(
    (state: RootState) => state.courseList.metadataLoading,
  );
  const isSubmitting = useAppSelector(
    (state: RootState) => state.createUpdateCourse.loadingCreate,
  );

  const form = useForm<CourseFormData>({
    resolver: zodResolver(courseFormSchema),
    defaultValues: emptyCourseFormData(),
    mode: "onChange",
  });

  useEffect(() => {
    dispatch(fetchCourseMetadataAction());
    return () => {
      dispatch(resetCreateUpdateCourseState());
    };
  }, [dispatch]);

  const submitForm = form.handleSubmit((data) => {
    dispatch(
      createCourseAction(formDataToPayload(data), (course) => {
        showNotification(
          "Course created",
          `"${course.title}" was created.`,
          "success",
        );
        navigate(`/admin/learning-center/courses/${course.uuid}/edit`);
      }),
    );
  });

  const handleCancel = () => {
    navigate("/admin/learning-center/courses");
  };

  return {
    form,
    metadata,
    metadataLoading,
    isSubmitting,
    submitForm,
    handleCancel,
  };
};
